import { Group, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';

import { formatDateTime, useAppNotification } from '@feature/utils';

import { useUserDocumentDelete, useUserDocumentDownload } from '../model/UserDocumentQuery';
import { UserDocumentSlot } from '../model/UserDocumentTypes';
import { UserDocumentSlotActions } from './UserDocumentSlotActions';
import { UserDocumentUploadModal } from './UserDocumentUploadModal';

interface UserDocumentSlotRowProps {
  slot: UserDocumentSlot;
}

export function UserDocumentSlotRow({ slot }: UserDocumentSlotRowProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const [uploadOpened, { open: openUpload, close: closeUpload }] = useDisclosure(false);
  const download = useUserDocumentDownload();
  const deleteDocument = useUserDocumentDelete();

  const uploadedDocument = slot.document;

  const onDownload = () =>
    download.mutate(
      { kind: slot.kind, fallbackFilename: uploadedDocument?.file.title ?? slot.kind },
      { onError: notification.error },
    );

  const onDelete = () =>
    deleteDocument
      .mutateAsync(slot.kind, {
        onError: notification.error,
        onSuccess: notification.success,
      })
      // Already reported through onError; the confirm dialog only awaits the settle to close.
      .catch(() => undefined);

  return (
    <Group
      gap="xs"
      align="center"
      justify="space-between"
      wrap="nowrap"
    >
      <Stack
        gap={2}
        miw={0}
      >
        <Text
          size="sm"
          c={uploadedDocument == null ? 'dimmed' : undefined}
          fs={uploadedDocument == null ? 'italic' : undefined}
        >
          {t(`userDocuments.kindEnum.${slot.kind}`)}: {uploadedDocument?.file.title ?? t('userDocuments.missing')}
        </Text>
        {uploadedDocument != null && (
          <Text
            size="xs"
            c="dimmed"
          >
            {t('userDocuments.uploadedAt')}: {formatDateTime(uploadedDocument.uploadedAt)}
          </Text>
        )}
      </Stack>
      <UserDocumentSlotActions
        hasDocument={uploadedDocument != null}
        onDownload={onDownload}
        isDownloading={download.isPending}
        onUpload={openUpload}
        onDelete={onDelete}
        isDeleting={deleteDocument.isPending}
      />
      {uploadOpened && (
        <UserDocumentUploadModal
          kind={slot.kind}
          onClose={closeUpload}
        />
      )}
    </Group>
  );
}
